import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Link } from '@mui/material';
import { styled } from '@mui/system';
import { motion, useInView } from 'framer-motion';
import axios from 'axios';

const AnimatedGlowBox = styled(motion(Box), {
  shouldForwardProp: (prop) => prop !== 'godMode',
})(({ theme, godMode }) => ({
  width: '380px',
  minHeight: '380px',
  padding: '30px',
  margin: '20px auto',
  // Without border-box the padding made the box 60px wider than its
  // centered wrapper, so it hung off to the right on mobile/tablet.
  boxSizing: 'border-box',
  // In God Mode the box goes transparent and GodCircleFour renders on a
  // separate stationary layer behind it (see render), so dragging the box
  // aside leaves the image in place for the viewer to see.
  backgroundColor: godMode ? 'transparent' : '#1a237e',
  borderRadius: '10px',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: '20px',
    width: '90%',
    minHeight: 'auto',
  },
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  color: '#FFFFFF',
  fontSize: '2rem',
  marginBottom: '10px',
  textShadow: `
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    1px 1px 0 #000
  `,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
}));

const CryptoItem = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  color: '#FFFFFF',
  fontSize: '1rem',
  margin: '4px 0',
  textShadow: `
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    1px 1px 0 #000
  `,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}));

const ToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  margin: '10px 0 16px',
}));

const ToggleLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  color: '#cc99ff',
  fontSize: '0.9rem',
  textShadow: `
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    1px 1px 0 #000
  `,
}));

const ToggleTrack = styled(motion.div)(({ theme }) => ({
  width: 80,
  height: 28,
  background: '#2a2a72',
  border: '2px solid #000',
  borderRadius: 20,
  position: 'relative',
  boxShadow: 'inset 0 0 6px rgba(0,0,0,0.4), 0 0 10px #7b1fa2',
  cursor: 'pointer',
}));

const ToggleKnob = styled(motion.div)(({ theme }) => ({
  width: 32,
  height: 24,
  background: '#cc66ff',
  border: '2px solid #000',
  borderRadius: 16,
  position: 'absolute',
  top: 0,
  left: 0,
  boxShadow: '0 2px 0 #000, 0 0 8px #ba55d3',
}));

const BuyCryptoOnline = ({ godMode = false }) => {
  const [movers, setMovers] = useState([]);
  const [showT100, setShowT100] = useState(false); // false = T250, true = T100
  const boxRef = useRef(null);
  const isInView = useInView(boxRef, { once: true, margin: '-100px' });

  useEffect(() => {
    const fetchMovers = async () => {
      try {
        // When showT100 is true, get top 100. When false, get top 250
        const limit = showT100 ? 100 : 250;
        const url = `/api/top-movers?limit=${limit}`;
        const response = await axios.get(url);
        setMovers(response.data.movers || []);
      } catch (error) {
        console.error('Error fetching top movers:', error);
      }
    };
    fetchMovers();
  }, [showT100]);

  return (
    <Box
      sx={{
        position: 'relative',
        margin: '20px auto',
        width: { xs: '90%', sm: '380px' },
      }}
    >
      {/* Stationary god circle layer: stays put while the box is dragged
          aside, so the viewer can see the image unobstructed. */}
      {godMode && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(https://media.satoshiadvance.com/images/GodTangle1.png)',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            backgroundSize: 'contain',
            borderRadius: '10px',
            pointerEvents: 'none',
          }}
        />
      )}
    <AnimatedGlowBox
      godMode={godMode}
      ref={boxRef}
      style={{ margin: 0, width: '100%' }}
      // Click-and-drag left/right (same feel as the other purple boxes)
      drag="x"
      dragMomentum={false}
      dragElastic={0.15}
      dragConstraints={{ left: -600, right: 600 }}
      whileDrag={{ cursor: 'grabbing' }}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1, boxShadow: '0 0 20px #9932CC, 0 0 30px #9932CC, 0 0 40px #9932CC' } : {}}
      whileHover={{
        boxShadow: '0 0 40px #BA55D3, 0 0 60px #BA55D3, 0 0 80px #BA55D3',
      }}
      whileTap={{
        boxShadow: '0 0 50px #9400D3, 0 0 70px #9400D3, 0 0 90px #9400D3',
      }}
      transition={{
        duration: 1.5,
        ease: 'easeInOut',
      }}
    >
      <TitleText>Buy Bitcoin & Crypto Online</TitleText>
      <ToggleContainer>
        <ToggleLabel>T100</ToggleLabel>
        <ToggleTrack
          role="switch"
          aria-checked={showT100}
          onClick={() => setShowT100((v) => !v)}
          whileTap={{ scale: 0.98 }}
        >
          <ToggleKnob
            animate={{ x: showT100 ? 0 : 48 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          />
        </ToggleTrack>
        <ToggleLabel>T250</ToggleLabel>
      </ToggleContainer>
      {movers.map((item, idx) => (
        <CryptoItem key={idx}>
          {item.symbol}: {item.percent_change_24h.toFixed(2)}%
        </CryptoItem>
      ))}
      <Box sx={{ mt: 2 }}>
        <Link
          href="https://www.gemini.com/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: '#cc66ff', fontFamily: "'Press Start 2P', cursive" }}
        >
          Buy on Gemini
        </Link>
      </Box>
    </AnimatedGlowBox>
    </Box>
  );
};

export default BuyCryptoOnline;
